import fetch from 'node-fetch'
import LyraAuth from './LyraAuth'
import './LyraAuth.css'
import './utils/Animation.css'
import Sentry, { fillTagsAndExtras } from './utils/Sentry'

/* eslint no-underscore-dangle: 0 */

/* eslint no-console: 0 */
class KrAuthenticate {
    /**
     * Constructor.
     *
     */
    constructor(publicKey, options = {}) {
        this.publicKey = publicKey
        this.options = options
        // Get sentry from option or new instance
        this.options.Sentry = options.Sentry || Sentry
        this.options.Sentry.setTag(
            'client',
            options.clientName != null ? options.clientName : 'KR-AUTHENTICATE',
        )
        this.options.Sentry.setTag('pdvKey', this.publicKey?.split(':')[0] || 'unknown')
    }

    /**
     * get browser device information
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    _buildDeviceBrowserDTO() {
        const device = {
            deviceType: 'BROWSER',
            language: window.navigator.language,
            userAgent: navigator.userAgent || 'unknown',
            colorDepth: window.screen.colorDepth.toString(),
            timeZoneOffset: new Date().getTimezoneOffset(),
            screenHeight: window.screen.height,
            screenWidth: window.screen.width,
            javaEnabled: navigator.javaEnabled(),
            acceptHeader: 'application/json', // will be overred by server
        }
        return {
            authenticationRequest: {
                device,
            },
            publicKey: this.publicKey,
        }
    }

    _buildInstructionResultSessionDTO(instructionResult) {
        return {
            authenticationRequest: {
                instructionResult,
            },
            publicKey: this.publicKey,
        }
    }

    /**
     * Add device information to authentication session on server
     * @param url the url to send browser info device collected info dto
     */
    addDeviceInformation(url) {
        this.options.Sentry.setExtra('operationUrl', url)
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            // credentials: 'include', //The value of the 'Access-Control-Allow-Origin' header in the response must not be the wildcard '*' when the request's credentials mode is 'include'.
            body: JSON.stringify(this._buildDeviceBrowserDTO()),
        })
    }

    /**
     * Add instruction result to authentication session on server
     * @param instructionResult the instruction result
     * @param url the url to send session with instruction result dto
     */
    addInstructionResultInformation(instructionResult, url) {
        this.options.Sentry.setExtra('operationUrl', url)
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            // credentials: 'include', //The value of the 'Access-Control-Allow-Origin' header in the response must not be the wildcard '*' when the request's credentials mode is 'include'.
            body: JSON.stringify(this._buildInstructionResultSessionDTO(instructionResult)),
        })
    }

    /**
     * Perform the authentication.
     * @param operationUrl  operationUrl the operationUrl provided by the response of the PCI call to V4/PCI/Authentication/CreatePayment or V4/PCI/Authentication/CreateSession
     * @param callback method called at the end of the authentication process (with or without payment)
     * @returns {Promise<void>}
     */
    authenticate(operationUrl, callback = undefined) {
        return (
            this.addDeviceInformation(operationUrl)
                .then((response) => response.json())
                // Execute instruction
                .then((data) =>
                    this.handleAuthenticationSessionResponseAnswer(data, operationUrl, callback),
                )
                .catch((error) => {
                    this.manageError('collecting device info', callback, error)
                })
        )
    }

    manageError(step, callback, error) {
        const errorMessage = `Error while ${step} : ${error}`
        console.error(errorMessage)

        // send to sentry
        this.options.Sentry.setTag('step', step)
        this.options.Sentry.captureException(error)

        if (typeof callback !== 'undefined') {
            callback(errorMessage)
        }
    }

    handleAuthenticationSessionResponseAnswer(data, url, callback) {
        fillTagsAndExtras(data)
        if (
            typeof data.answer.result !== 'undefined' &&
            typeof data.answer.result.value.responseType !== 'undefined' &&
            data.answer.result.value.responseType === 'INSTRUCTION'
        ) {
            this.executeInstructions(data, url, callback)
        } else if (typeof callback !== 'undefined') {
            if (this.options.debug) {
                console.log(`result received : ${JSON.stringify(data)}`)
            }
            try {
                callback(data.answer)
            } catch (error) {
                this.manageError('Executing callback', callback, error)
            }
        }
    }

    executeInstructions(data, url, callback) {
        if (this.options.debug) {
            console.log(`execute instruction with data : ${JSON.stringify(data)}}`)
        }
        const lyraAuth = new LyraAuth(this.options)
        lyraAuth.delegate(
            data.answer.result,
            (res) => {
                this.addInstructionResultInformation(res, url)
                    .then((response) => response.json())
                    // eslint-disable-next-line no-shadow
                    .then((data) =>
                        this.handleAuthenticationSessionResponseAnswer(data, url, callback),
                    )
                    .catch((error) => {
                        this.manageError('executing instruction info', callback, error)
                    })
            },
            null,
            null,
            true,
        )
    }
}

/* ************************************* */
/* ********      EXPORTS        ******** */
/* ************************************* */
export default KrAuthenticate
